/**
 * Resource Section Form Modal
 *
 * Adds or edits one section: title, notes and an ordered list of links and
 * files. A long section is a lot of typing, so a stray click or Escape must not
 * discard it: only Cancel closes the modal.
 *
 * Picked files upload on Save, not on pick, so cancelling leaves nothing behind.
 */

import { useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { arrayMove } from '@dnd-kit/sortable';
import { Modal } from '../ui';
import { SortableItem, SortableList } from './Sortable';
import {
  createResourceSection,
  updateResourceSection,
  uploadResourceFile,
  type ResourceSection,
  type ResourceSectionInput,
} from '../../api/resources';
import { getErrorMessage } from '../../api/client';
import { useOrganization } from '../../context/OrganizationContext';
import { ALLOWED_EXTENSIONS, formatFileSize, validateFile } from '../../utils/fileFormatting';

interface ItemRow {
  /** Stable identity for React keys and dnd-kit; a row may not have an id yet. */
  uid: string;
  title: string;
  url: string;
  notes: string;
  /** Set on a file row, whether or not it has uploaded yet. */
  fileName?: string;
  fileSize?: number;
  /** The stored file. Stays set after a failed save so a retry won't re-upload. */
  fileId?: string;
  /** Picked and waiting for Save. */
  pendingFile?: File;
}

const isFileRow = (row: ItemRow) => row.fileName !== undefined;

let uidCounter = 0;
const nextUid = (): string => `item-${++uidCounter}`;

/** "Crew handbook.pdf" becomes "Crew handbook". */
const titleFromFileName = (name: string) => name.replace(/\.[^.]+$/, '');

interface ResourceSectionFormModalProps {
  /** The section to edit, or null to add one. */
  section: ResourceSection | null;
  canUploadFiles: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export default function ResourceSectionFormModal({
  section,
  canUploadFiles,
  onClose,
  onSaved,
}: ResourceSectionFormModalProps) {
  const { isOrgOwner } = useOrganization();
  const [title, setTitle] = useState(section?.title ?? '');
  const [notes, setNotes] = useState(section?.notes ?? '');
  const [rows, setRows] = useState<ItemRow[]>(() =>
    (section?.items ?? []).map((item) => ({
      uid: nextUid(),
      title: item.title,
      url: item.url ?? '',
      notes: item.notes ?? '',
      fileName: item.file?.fileName,
      fileSize: item.file?.fileSize,
      fileId: item.file?.id,
    }))
  );
  const [addedUid, setAddedUid] = useState<string | null>(null);
  const [savingLabel, setSavingLabel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const saving = savingLabel !== null;

  const updateRow = (uid: string, field: 'title' | 'url' | 'notes', value: string) => {
    setRows((current) => current.map((row) => (row.uid === uid ? { ...row, [field]: value } : row)));
  };

  const addLink = () => {
    const uid = nextUid();
    setRows((current) => [...current, { uid, title: '', url: '', notes: '' }]);
    setAddedUid(uid);
  };

  const handleFilesPicked = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = ''; // so picking the same file again still fires

    const problems: string[] = [];
    const added: ItemRow[] = [];
    for (const file of picked) {
      const check = validateFile(file);
      if (check.valid) {
        added.push({
          uid: nextUid(),
          title: titleFromFileName(file.name),
          url: '',
          notes: '',
          fileName: file.name,
          fileSize: file.size,
          pendingFile: file,
        });
      } else {
        problems.push(`${file.name}: ${check.error}`);
      }
    }

    setRows((current) => [...current, ...added]);
    setError(problems.length > 0 ? problems.join(' ') : null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // A link row left completely blank is an unused "Add link", not a mistake.
    const filled = rows.filter(
      (row) => isFileRow(row) || row.title.trim() || row.url.trim() || row.notes.trim()
    );

    if (filled.some((row) => !isFileRow(row) && (!row.title.trim() || !row.url.trim()))) {
      setError('Every link needs a title and a URL.');
      return;
    }

    setError(null);

    try {
      // One at a time: each can be 10MB, and a failure names the file it hit.
      const waiting = filled.filter((row) => row.pendingFile);
      const uploaded = new Map<string, string>();
      for (const [index, row] of waiting.entries()) {
        setSavingLabel(`Uploading ${index + 1} of ${waiting.length}...`);
        try {
          const file = await uploadResourceFile(row.pendingFile!);
          uploaded.set(row.uid, file.id);
          setRows((current) =>
            current.map((r) =>
              r.uid === row.uid ? { ...r, fileId: file.id, pendingFile: undefined } : r
            )
          );
        } catch (err) {
          throw new Error(`${row.fileName}: ${getErrorMessage(err)}`);
        }
      }

      setSavingLabel('Saving...');
      const data: ResourceSectionInput = {
        title: title.trim(),
        notes: notes.trim() || null,
        items: filled.map((row) => {
          const itemNotes = row.notes.trim() || null;
          return isFileRow(row)
            ? {
                title: row.title.trim() || titleFromFileName(row.fileName!),
                fileId: row.fileId ?? uploaded.get(row.uid),
                notes: itemNotes,
              }
            : { title: row.title.trim(), url: row.url.trim(), notes: itemNotes };
        }),
      };

      if (section) {
        await updateResourceSection(section.id, data);
      } else {
        await createResourceSection(data);
      }
      onSaved();
    } catch (err) {
      setError(getErrorMessage(err));
      setSavingLabel(null);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="2xl"
      labelledBy="resource-section-form-title"
      closeOnOverlayClick={false}
      closeOnEscape={false}
      contentClassName="max-h-[90vh] flex flex-col"
    >
      <form onSubmit={handleSubmit} className="flex flex-col min-h-0">
        <div className="px-6 py-4 border-b border-hairline">
          <h3 id="resource-section-form-title" className="text-lg font-semibold text-ink">
            {section ? 'Edit section' : 'Add section'}
          </h3>
          <p className="text-sm text-ink-body mt-1">
            Everyone in your organization can read this page, so keep passwords off it.
          </p>
        </div>

        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          {error && (
            <div role="alert" className="p-3 bg-white border border-hairline border-l-[3px] border-l-program text-sm text-program-dark">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="resource-section-title" className="block text-sm font-medium text-ink mb-1">
              Title
            </label>
            <input
              id="resource-section-title"
              type="text"
              required
              maxLength={200}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="input"
              placeholder="Getting paid"
            />
          </div>

          <div>
            <label htmlFor="resource-section-notes" className="block text-sm font-medium text-ink mb-1">
              Notes
            </label>
            <textarea
              id="resource-section-notes"
              rows={2}
              maxLength={2000}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="input"
            />
          </div>

          <div>
            <h4 className="text-sm font-medium text-ink mb-2">Links and files</h4>
            <SortableList
              ids={rows.map((row) => row.uid)}
              onMove={(oldIndex, newIndex) => setRows((current) => arrayMove(current, oldIndex, newIndex))}
            >
              <div className="space-y-2">
                {rows.map((row, index) => {
                  const kind = isFileRow(row) ? 'File' : 'Link';
                  const name = row.title || `${kind.toLowerCase()} ${index + 1}`;
                  return (
                    <SortableItem
                      key={row.uid}
                      id={row.uid}
                      handleLabel={`Reorder ${name}`}
                      className="bg-white border border-hairline p-3"
                    >
                      {(handle) => (
                        <div className="flex items-start gap-2">
                          {handle}
                          <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <input
                              type="text"
                              aria-label={`${kind} ${index + 1} title`}
                              placeholder="Title"
                              maxLength={200}
                              autoFocus={row.uid === addedUid}
                              value={row.title}
                              onChange={(e) => updateRow(row.uid, 'title', e.target.value)}
                              className="input"
                            />
                            {isFileRow(row) ? (
                              <p className="min-w-0 self-center text-sm text-ink-body truncate" title={row.fileName}>
                                {row.fileName} ({formatFileSize(row.fileSize ?? 0)})
                              </p>
                            ) : (
                              // Not type="url": that would refuse a bare "espn.com",
                              // which the server accepts as https.
                              <input
                                type="text"
                                inputMode="url"
                                autoCapitalize="none"
                                autoCorrect="off"
                                aria-label={`Link ${index + 1} URL`}
                                placeholder="URL"
                                maxLength={2048}
                                value={row.url}
                                onChange={(e) => updateRow(row.uid, 'url', e.target.value)}
                                className="input"
                              />
                            )}
                            <input
                              type="text"
                              aria-label={`${kind} ${index + 1} note`}
                              placeholder="Note (optional)"
                              maxLength={2000}
                              value={row.notes}
                              onChange={(e) => updateRow(row.uid, 'notes', e.target.value)}
                              className="input sm:col-span-2"
                            />
                          </div>
                          <button
                            type="button"
                            aria-label={`Remove ${name}`}
                            onClick={() => setRows((current) => current.filter((r) => r.uid !== row.uid))}
                            className="w-8 h-8 shrink-0 flex items-center justify-center text-ink-muted hover:text-program-dark focus-ring"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </SortableItem>
                  );
                })}
              </div>
            </SortableList>

            <div className="mt-2 flex gap-2">
              <button type="button" onClick={addLink} className="btn-secondary">
                Add link
              </button>
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                disabled={!canUploadFiles}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add file
              </button>
              <input
                ref={fileInput}
                type="file"
                multiple
                accept={ALLOWED_EXTENSIONS}
                onChange={handleFilesPicked}
                aria-label="Choose files"
                className="hidden"
              />
            </div>
            <p className="mt-2 text-sm text-ink-muted">
              {canUploadFiles ? (
                'PDF, Word, Excel, PowerPoint, text, CSV or image, up to 10 MB each.'
              ) : (
                <>
                  Uploading files is included on paid plans. Links work on every plan.
                  {isOrgOwner && (
                    <>
                      {' '}
                      <Link to="/admin/settings/billing" className="font-medium text-ink underline">
                        See plans
                      </Link>
                    </>
                  )}
                </>
              )}
            </p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-hairline flex justify-end gap-3">
          <button type="button" onClick={onClose} disabled={saving} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="btn-primary">
            {savingLabel ?? 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
